import moment from 'moment';
import { jwtDecode } from 'jwt-decode';
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api, { exceptionNotificationAPI } from '../services/api';

const AuthContext = createContext({});

const isTokenExpired = (token) => {
    try {
        const decoded = jwtDecode(token);
        return decoded.exp < Date.now() / 1000;
    } catch {
        return true;
    }
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);

    const [loading, setLoading] = useState(true);

    const clearSession = useCallback(() => {
        api.defaults.headers.authorization = '';
        localStorage.removeItem('@MonitorNogleApp:token');
        localStorage.removeItem('@MonitorNogleApp:user');
        localStorage.removeItem('@MonitorNogleApp:userName');
        setUser(null);
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('@MonitorNogleApp:token');
        const userStored = localStorage.getItem('@MonitorNogleApp:user');

        if (token && userStored) {
            if (isTokenExpired(token)) {
                clearSession();
            } else {
                api.defaults.headers.authorization = `Bearer ${token}`;
                setUser({ token, user: JSON.parse(userStored) });
            }
        }

        setLoading(false);
    }, [clearSession]);

    const signIn = async ({ email, password }) => {
        try {
            const response = await api.post('/authenticate', { email, password });
            const r = response.data;
            api.defaults.headers.authorization = `Bearer ${r.token}`;
            localStorage.setItem('@MonitorNogleApp:token', r.token);
            localStorage.setItem('@MonitorNogleApp:user', JSON.stringify(r));
            localStorage.setItem('@MonitorNogleApp:userName', email);
            setUser({ token: r.token, user: r });
            return response;
        } catch (error) {
            if (error.response && error.response.status === 401) {
                return { error: error.response.data.message };
            } else {
                exceptionNotificationAPI(error);
            }
        }
    };

    const signOut = async () => {
        setLoading(true); 

        api.defaults.headers.authorization = '';
        localStorage.removeItem('@MonitorNogleApp:token');
        localStorage.removeItem('@MonitorNogleApp:user');
        localStorage.removeItem('@MonitorNogleApp:userName');

        await new Promise(resolve => setTimeout(resolve, 50));

        setUser(null);
        
        setLoading(false); 
    }

    const [headerExpanded, setHeaderExpanded] = useState(localStorage.getItem('@MonitorNogleApp:isExpanded'));

    useEffect(() => {
        localStorage.setItem('@MonitorNogleApp:isExpanded', null);
        setHeaderExpanded(null);
    }, [])

    const headerExpand = () => {
        localStorage.setItem('@MonitorNogleApp:isExpanded', moment());
        setHeaderExpanded(localStorage.getItem('@MonitorNogleApp:isExpanded'));
    }

    useEffect(() => {
        setHeaderExpanded(localStorage.getItem('@MonitorNogleApp:isExpanded'));
    }, [localStorage.getItem('@MonitorNogleApp:isExpanded')]);

    const [faturamentoOpen, setFaturamentoOpen] = useState(false);

    const faturamentoOpenModal = () => {
        setFaturamentoOpen(!faturamentoOpen);
    }

    return (
        <AuthContext.Provider value={{ user: user?.user, loading, signIn, signOut, headerExpand, headerExpanded, faturamentoOpenModal, faturamentoOpen }}>
            {children}
        </AuthContext.Provider>
    );
};

export function useAuth() {
    return useContext(AuthContext);
}